import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink } from 'lucide-react';
import { numberLocale } from '@/lib/language';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { useNotifications } from '@/contexts/NotificationContext';
import { ProductStatusPill } from '../ProductStatusPill';
import type { WorkbenchProduct, ReviewDecision } from '@/hooks/useWorkbench';

interface CardHeaderProps {
  product: WorkbenchProduct;
  /**
   * Der aktuelle Bearbeitungsstand des optimierten Titels
   * (`editState.optimized_title`) - dieselbe Quelle, aus der Kachel und Diff
   * lesen. Ein zweiter Titelstand auf einer Karte waere genau der Fehler, den
   * `kopftitelMarke` einmal geheilt hat.
   */
  optimizedTitle: string;
  reviewDecision: ReviewDecision;
  /** 1-basiert. */
  position: number;
  total: number;
  /** true = die Gesamtzahl ist nur eine Untergrenze, nicht der Feed-Bestand. */
  totalUnknown?: boolean;
}

/**
 * Kopfzeile der Karte: Identitaet und Ort, sonst nichts.
 *
 * Was hier bewusst NICHT mehr steht (Spec 4.1 und 13):
 *   - die Punktzahl. Sie stand bis hierher dreimal auf der Karte; jetzt an
 *     genau einer Stelle, links unten.
 *   - der Thumbnail. Das einzige Bild der Karte gehoert in die Kachel, wo es
 *     zeigt, wie das Produkt beim Kunden ankommt. Ein Vorher/Nachher-Paar
 *     waere ohnehin sinnlos: eine Optimierung aendert `image_link` nie.
 *   - die Pille "Offen". Sie sagt dasselbe wie der aktive Reiter. Gezeigt
 *     werden nur ueberraschende Zustaende.
 *   - das Abzeichen "Bearbeitet". Dieselbe Aussage steht seit Etappe 5 als
 *     "Deine Fassung" an der Kachel, also an dem Text, den sie meint. Zwei
 *     Marken fuer denselben Sachverhalt auf einer Karte ist genau das Muster,
 *     das Abschnitt 13 abbaut. Das karten-weite Signal traegt weiterhin das
 *     Kartenband (`getBorderClass` in `WorkbenchProductCard`).
 *
 * Titel und Produkt-ID (T3, 08.09.2026): der Kopf zeigt seit hier den
 * OPTIMIERTEN Titel (`optimizedTitle`, aus `editState`), nicht mehr
 * `product.title`. Die Leistenzeile daneben zeigt denselben optimierten
 * Titel - ein Feldwechsel im Kopf allein haette die Verwechslungsgefahr, gegen
 * die `kopftitelMarke` 2026-08-23 gebaut wurde, nur umgedreht (Leiste und Kopf
 * zeigen jetzt DASSELBE Feld, die Marke sagt, welcher Stand gemeint ist).
 * `product.product_id` verlaesst dabei den Klartext: die nackte Tilde-ID war
 * fuer den Kunden ohnehin wertlos und stand nur als Debug-Information da. Der
 * Kopier-Knopf traegt sie als `title`-Tooltip und auf Klick in die
 * Zwischenablage.
 */
export function CardHeader({
  product,
  optimizedTitle,
  reviewDecision,
  position,
  total,
  totalUnknown,
}: CardHeaderProps) {
  const { t, i18n } = useTranslation('workbench');
  const locale = numberLocale(i18n?.language);
  const { kopftitelMarke } = useEntwurf();
  const { toast } = useNotifications();

  const displayTitle = optimizedTitle || product.optimized_title || product.title || '—';

  const handleCopyId = useCallback(async () => {
    const id = product.product_id;
    if (!id) return;
    try {
      if (!navigator.clipboard) {
        throw new Error('clipboard-unavailable');
      }
      await navigator.clipboard.writeText(id);
      toast({ tone: 'success', title: t('card.idCopied') });
    } catch {
      toast({ title: t('card.idCopyFailed'), variant: 'destructive' });
    }
  }, [product.product_id, toast, t]);

  // Marke und Optimierungsdatum sind nullable. Was fehlt, faellt weg, statt
  // als leerer Trenner stehen zu bleiben. Die Produkt-ID steht hier NICHT
  // mehr im Klartext - sie traegt nur noch den Kopier-Knopf.
  const metaParts = [
    product.brand,
    product.last_optimized_at
      ? t('card.optimizedAt', {
          date: new Date(product.last_optimized_at).toLocaleDateString(locale),
        })
      : null,
  ].filter((part): part is string => Boolean(part && String(part).trim() !== ''));

  return (
    <div className="flex flex-shrink-0 items-center gap-3 border-b border-border bg-muted/30 px-4 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {kopftitelMarke === 'mitOptimiert' && (
            /* `flex-shrink-0`, damit die Marke nicht selbst weggekuerzt wird -
               eine halbe Marke ist schlimmer als keine. `text-foreground/70`
               statt `text-muted-foreground`: auf der `bg-muted/30`-Flaeche des
               Kopfes ist die Hausantwort das Alpha auf foreground, nicht ein
               zweites Alpha auf einem ohnehin gedaempften Token. */
            <span
              data-testid="card-header-optimized-mark"
              className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-foreground/70"
            >
              {t('card.optimizedMark')}
            </span>
          )}
          <p className="truncate text-sm font-semibold leading-snug">{displayTitle}</p>
          {product.link && (
            <a
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('card.openProductPage')}
              className="flex-shrink-0 text-muted-foreground transition-colors hover:text-primary"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-1">
          <p data-testid="card-header-meta" className="truncate text-xs text-muted-foreground">
            {metaParts.join(' · ')}
          </p>
          {product.product_id && (
            <button
              type="button"
              onClick={handleCopyId}
              aria-label={t('card.copyIdAria')}
              title={product.product_id}
              className="flex-shrink-0 text-muted-foreground transition-colors hover:text-primary"
            >
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <ProductStatusPill
          approvalStatus={reviewDecision}
          uploadStatus={product.upload_status ?? null}
          driftDetectedAt={product.merchant_drift_detected_at ?? null}
          hideWhenPending
        />
        <span
          data-testid="card-header-position"
          className="whitespace-nowrap text-xs tabular-nums text-muted-foreground"
        >
          {t(totalUnknown ? 'card.positionUnknown' : 'card.position', {
            current: position.toLocaleString(locale),
            total: total.toLocaleString(locale),
          })}
        </span>
      </div>
    </div>
  );
}
